import { h } from '@omni-torn/ui';
import { filterWars, groupWars, tornWarReportUrl, warStats, WAR_RECENT_HOURS, WAR_SOON_HOURS, type WarLine } from '@omni-torn/shared';
import type { RefRankedWar, TornRefAnswer, WarReport, WarReportSide } from '@omni-torn/shared/torn-ref';
import { fromTorn, oops, ref } from '../torn-lists';
import { ago, box, duration, ext, facts, kids, loading, num, profileUrl, table, tct } from '../ui';

/**
 * The website's "Ranked wars" page (website roadmap S15: FF Scouter's Active Ranked Wars, TornStats' ranked wars): every
 * ranked war Torn lists — going on (score bars, the lead needed now, when it ends if the lead stays), starting within 12
 * hours, starting later, and ended in the last 48 hours — with each war's report (/rankedwars/{id}: both sides, every
 * member's attacks and score, ranks and rewards) and "compare them" (the Factions page, S14).
 * Data: GET /api/torn/rankedwars (Torn's list, read by the Omni-Torn server with its own key, kept 15 minutes) and
 * GET /api/torn/rankedwarreport?arg={id} (asked only when someone opens a report; a finished war's kept a week in memory).
 * Nothing costs the player's key; nothing is kept by this page.
 */

/** A faction's name → its profile on this site (pages/factions.ts), where Torn's page is linked too. */
export const profileLink = (id: number | null, name?: string | null): HTMLElement => (id ? h('a', { href: `/factions/${id}`, 'data-link': true }, name || `Faction ${id}`) : h('span', { class: 'muted' }, 'nobody'));

/** Two coloured parts, each side's share of the war's score. */
function scoreBar(l: WarLine): HTMLElement | null {
  if (!l.shares) return null;
  const [x, y] = l.war.factions;
  const a = h('span', { class: 'rw-score-a' });
  const b = h('span', { class: 'rw-score-b' });
  a.style.width = `${l.shares[0] * 100}%`;
  b.style.width = `${l.shares[1] * 100}%`;
  return h('div', { class: 'rw-score', role: 'img', 'aria-label': `${x!.name} ${num(x!.score)}, ${y!.name} ${num(y!.score)}` }, a, b);
}

/** How far the lead is toward the lead needed now. */
function progressBar(l: WarLine): HTMLElement | null {
  if (l.progress === null) return null;
  const fill = h('span', {});
  fill.style.width = `${l.progress * 100}%`;
  return h('div', { class: 'rw-progress', title: 'The lead, of the lead needed to win now' }, fill);
}

/** One war: the two sides and their scores, the bars, and a line with when, the target and the links. */
export function warRow(l: WarLine, now: number): HTMLElement {
  const w = l.war;
  const [x, y] = w.factions;
  const winner = l.phase === 'ended' ? l.leader : null;
  const sides = h(
    'div',
    { class: 'war-sides' },
    ...w.factions.flatMap((f, i) => [
      i ? h('span', { class: 'muted' }, ' vs ') : null,
      profileLink(f.id, f.name),
      l.phase === 'soon' || l.phase === 'later' ? null : h('strong', { class: (winner ?? l.leader) === f ? 't-green' : '' }, ` ${num(f.score)}`),
    ]),
  );
  const when =
    l.phase === 'soon' || l.phase === 'later'
      ? `starts ${tct(w.start * 1000)} TCT (in ${duration(w.start - now)})`
      : l.phase === 'ended'
        ? `ended ${ago(w.end! * 1000)}${winner ? ` · ${winner.name} won` : ''}`
        : `started ${ago(w.start * 1000)}`;
  const race =
    l.phase === 'on' && l.needed !== null
      ? h(
          'div',
          { class: 'small', 'data-war-race': '' },
          l.leader ? `${l.leader.name} leads by ${num(l.lead)} of the ${num(l.needed)} needed now (${num((l.progress ?? 0) * 100)}%)` : `Level — the ${num(l.needed)} lead needed now`,
          l.endsAt ? ` · ends ${l.endsAt <= now ? 'now' : `${tct(l.endsAt * 1000)} TCT`} if the lead stays` : '',
        )
      : null;
  return h(
    'li',
    { class: 'war-row', 'data-war': String(w.id) },
    sides,
    l.phase === 'on' || l.phase === 'ended' ? scoreBar(l) : null,
    l.phase === 'on' ? progressBar(l) : null,
    race,
    h(
      'div',
      { class: 'muted small' },
      w.target ? `Target ${num(w.target)} · ` : '',
      when,
      w.factions.some((f) => f.chain) ? ` · chains ${w.factions.map((f) => num(f.chain)).join(' / ')}` : '',
      ...(x && y ? [' · ', h('a', { href: `/factions/compare/${x.id}/${y.id}`, 'data-link': true }, 'compare them')] : []),
      ...(l.phase === 'ended' ? [' · ', h('a', { href: `/rankedwars/${w.id}`, 'data-link': true, 'data-war-report-link': '' }, 'report')] : []),
    ),
  );
}

/** A list of wars, the first `cap` shown and a button for the rest. */
function warList(lines: WarLine[], now: number, cap = 50): HTMLElement {
  const ul = h('ul', { class: 'plain-list wars' }, ...lines.slice(0, cap).map((l) => warRow(l, now)));
  if (lines.length <= cap) return ul;
  const more = h('button', { type: 'button', class: 'btn-small' }, `Show all ${num(lines.length)}`);
  more.addEventListener('click', () => {
    ul.replaceChildren(...lines.map((l) => warRow(l, now)));
    more.remove();
  });
  return h('div', {}, ul, more);
}

/** The Factions page's box of wars (pages/torn.ts appendFactionLists): going on, starting within 12 hours, ended. */
export function factionsWarsBody(a: TornRefAnswer<'rankedwars'>): Array<Node | string> {
  const now = Date.now() / 1000;
  const g = groupWars(a.data, now);
  const earlier = a.data.filter((w) => w.end && w.end <= now).length - g.ended.length;
  return kids(
    h('h3', { class: 'sub' }, `Going on (${g.on.length})`),
    g.on.length ? warList(g.on, now, 20) : h('p', { class: 'muted' }, 'No ranked wars right now.'),
    g.soon.length ? h('h3', { class: 'sub' }, `Starting within ${WAR_SOON_HOURS} hours (${g.soon.length})`) : null,
    g.soon.length ? warList(g.soon, now, 20) : null,
    g.ended.length ? h('h3', { class: 'sub' }, `Ended (${g.ended.length})`) : null,
    g.ended.length ? warList(g.ended, now, 20) : null,
    earlier > 0 ? h('p', { class: 'muted small' }, `${num(earlier)} more ended over ${WAR_RECENT_HOURS} hours ago.`) : null,
    h('p', {}, h('a', { href: '/rankedwars', 'data-link': true }, 'Every ranked war, with scores, reports and statistics →')),
    fromTorn(a),
  );
}

// ---------------------------------------------------------------- the page

export async function renderRankedWars(view: HTMLElement, path: string): Promise<void> {
  const one = /^\/rankedwars\/(\d+)\/?$/.exec(path)?.[1];
  if (one) return renderReport(view, Number(one));
  const body = h('div', {}, loading('Reading the ranked wars…'));
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Ranked wars'), h('p', { class: 'muted' }, 'Every ranked war Torn lists: going on, about to start and just ended — and each war’s report.')),
    body,
  );
  let a: TornRefAnswer<'rankedwars'>;
  try {
    a = await ref('rankedwars');
  } catch (err) {
    body.replaceChildren(oops(err, () => renderRankedWars(view, path)));
    return;
  }
  let q = '';
  const lists = h('div', {});
  const count = h('span', { class: 'muted small', role: 'status' });
  const search = h('input', { type: 'search', placeholder: 'Find a faction (name or id)…', class: 'filter', 'aria-label': 'Find a faction', 'data-war-filter': '' }) as HTMLInputElement;
  search.addEventListener('input', () => ((q = search.value), draw()));
  const draw = () => {
    const now = Date.now() / 1000;
    const wars: RefRankedWar[] = filterWars(a.data, q);
    const g = groupWars(wars, now);
    count.textContent = q ? `${num(g.on.length + g.soon.length + g.later.length + g.ended.length)} wars match` : '';
    const section = (id: string, title: string, lines: WarLine[], empty: string) => box(`${title} (${lines.length})`, lines.length ? warList(lines, now) : h('p', { class: 'muted' }, empty), { id });
    lists.replaceChildren(
      ...kids(
        section('wars-on', 'Going on', g.on, q ? 'None of theirs.' : 'No ranked wars right now.'),
        section('wars-soon', `Starting within ${WAR_SOON_HOURS} hours`, g.soon, 'None.'),
        g.later.length ? section('wars-later', 'Starting later', g.later, 'None.') : null,
        section('wars-ended', `Ended in the last ${WAR_RECENT_HOURS} hours`, g.ended, 'None that Torn still lists.'),
      ),
    );
  };
  draw();
  body.replaceChildren(statsBox(a, Date.now() / 1000), h('div', { class: 'toolbar' }, search, count), lists, fromTorn(a));
}

function statsBox(a: TornRefAnswer<'rankedwars'>, now: number): HTMLElement {
  const s = warStats(groupWars(a.data, now));
  const vs = (l: WarLine | null) => (l ? h('span', {}, ...l.war.factions.flatMap((f, i) => [i ? ' vs ' : '', profileLink(f.id, f.name)]), h('span', { class: 'muted' }, ` — lead ${num(l.lead)}${l.needed ? ` of ${num(l.needed)}` : ''}`)) : '—');
  return box(
    'At a glance',
    facts([
      ['Going on', h('span', { 'data-war-stat': 'on' }, `${num(s.on)} ${s.on === 1 ? 'war' : 'wars'} · ${num(s.factions)} factions`)],
      [`Starting within ${WAR_SOON_HOURS} h`, h('span', { 'data-war-stat': 'soon' }, num(s.soon))],
      ['Starting later', num(s.later)],
      [`Ended in the last ${WAR_RECENT_HOURS} h`, h('span', { 'data-war-stat': 'ended' }, num(s.ended))],
      ['Median target', num(s.medianTarget)],
      ['Closest to its end', vs(s.closest)],
      ['Most one-sided', vs(s.biggestLead)],
      ['Tightest race', vs(s.tightest)],
    ]),
    { id: 'war-stats' },
  );
}

// ---------------------------------------------------------------- a war's report

async function renderReport(view: HTMLElement, id: number): Promise<void> {
  const body = h('div', {}, loading('Asking for the war’s report…'));
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Ranked war report'), h('p', {}, h('a', { href: '/rankedwars', 'data-link': true }, '← Every ranked war'))),
    body,
  );
  document.title = `Ranked war ${id} · Omni-Torn`;
  let a: TornRefAnswer<'rankedwarreport'>;
  try {
    a = await ref('rankedwarreport', String(id));
  } catch (err) {
    body.replaceChildren(oops(err, () => renderReport(view, id)));
    return;
  }
  const r = a.data;
  const [x, y] = r.factions;
  const winner = r.factions.find((f) => f.id === r.winner) ?? null;
  body.replaceChildren(
    box(
      x && y ? h('h2', {}, profileLink(x.id, x.name), ' vs ', profileLink(y.id, y.name)) : 'The war',
      [
        facts([
          ['Result', h('span', { 'data-war-report': 'result' }, r.end ? (winner ? `${winner.name} won${r.forfeit ? ' (the other side forfeited)' : ''}` : 'Ended') : 'Not over yet')],
          ['Score', r.factions.map((f) => `${f.name} ${num(f.score)}`).join(' · ')],
          ['Started', r.start ? `${new Date(r.start * 1000).toISOString().slice(0, 16).replace('T', ' ')} TCT` : '—'],
          ['Ended', r.end ? `${new Date(r.end * 1000).toISOString().slice(0, 16).replace('T', ' ')} TCT (${duration(r.end - (r.start ?? r.end))})` : '—'],
        ]),
        h(
          'p',
          { class: 'small' },
          ...(x && y ? [h('a', { href: `/factions/compare/${x.id}/${y.id}`, 'data-link': true }, 'Compare them'), ' · '] : []),
          ext(tornWarReportUrl(id), 'Torn’s own report'),
        ),
      ],
      { id: 'war-report' },
    ),
    ...r.factions.map((s) => sideBox(s, r)),
    fromTorn(a),
  );
}

function sideBox(s: WarReportSide, r: WarReport): HTMLElement {
  const items = s.rewards?.items ?? [];
  return box(
    `${s.name}${r.winner === s.id ? ' — won' : ''}`,
    [
      facts([
        ['Score', num(s.score)],
        ['Attacks', num(s.attacks)],
        ['Rank', s.rank ? `${s.rank.before ?? '—'} → ${s.rank.after ?? '—'}` : '—'],
        ['Rewards', s.rewards ? [s.rewards.respect ? `${num(s.rewards.respect)} respect` : '', s.rewards.points ? `${num(s.rewards.points)} points` : '', ...items.map((i) => `${num(i.quantity)}× ${i.name}`)].filter(Boolean).join(' · ') || 'none' : '—'],
      ]),
      table(
        s.members,
        [
          { label: 'Member', get: (m) => h('span', {}, h('a', { href: `/lookup/${m.id}`, 'data-link': true }, m.name), ' ', ext(profileUrl(m.id), '↗')), sort: (m) => m.name.toLowerCase() },
          { label: 'Level', get: (m) => num(m.level), sort: (m) => m.level ?? 0, cls: 'r' },
          { label: 'Attacks', get: (m) => num(m.attacks), sort: (m) => m.attacks, cls: 'r' },
          { label: 'Score', get: (m) => num(m.score, 2), sort: (m) => m.score, cls: 'r' },
          { label: 'Share', get: (m) => (s.score > 0 ? `${num((m.score / s.score) * 100, 1)}%` : '—'), sort: (m) => m.score, cls: 'r' },
        ],
        { initial: 3, empty: 'Torn listed no members.' },
      ),
    ],
    { id: `war-side-${s.id}` },
  );
}
